/**
 * Home — the public landing page for Tom Miller Services / Business Builder.
 *
 * Ported 2026-09-02 from the Claude Design artboard
 * `biz_in_a_folder_tommillerservices/clients/tommillerservices/brand/design/Home.dc.html`.
 * Section copy and arrays live in `./home/homeContent.ts`; each band is a
 * component under `./home/`. The site navbar/footer are rendered here like every
 * other page (see the note above `<Routes>` in App.tsx) — the artboard's own
 * top nav is NOT ported.
 *
 * Anchors (in order): #hero #problem #platform #modules #systems #chain
 * #stateofplay #social #pricing (+ #ownership-note) #program #token #about
 * #proof (alias #where-this-is-going) #contact (alias #cta-section).
 *
 * #token is MEMBER-ONLY — the band self-gates on login and the logged-out page
 * carries no investment language anywhere (homeContent.ts explains why).
 */
import { Box } from "@chakra-ui/react";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { NavbarWithCallToAction } from "../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "./seometadata/components/SEOHead";
import { DevSectionBadges } from "../components/DevSectionBadges";
import AdminModuleLinks from "../components/AdminModuleLinks";
import { useHomeTokens } from "./home/primitives";
import { Hero } from "./home/Hero";
import { Problem } from "./home/Problem";
import { Platform } from "./home/Platform";
import { Modules } from "./home/Modules";
import { Systems } from "./home/Systems";
import { Chain } from "./home/Chain";
import { StateOfPlay } from "./home/StateOfPlay";
import { Social } from "./home/Social";
import { Pricing } from "./home/Pricing";
import { Program } from "./home/Program";
import { Token } from "./home/Token";
import { About } from "./home/About";
import { Proof } from "./home/Proof";
import { Contact } from "./home/Contact";

const Home = () => {
  const location = useLocation();
  const t = useHomeTokens();

  /*
    Scroll to `#<section-id>` when the page is reached with a hash — the footer's
    "Contact" link is `/#cta-section`, the hero's asterisk is `/#ownership-note`.
    React Router does not restore hash scroll position itself, so without this
    the link lands at the top of Home. rAF lets the sections paint first.
  */
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const raf = requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => cancelAnimationFrame(raf);
  }, [location.hash]);

  return (
    <Box bg={t.bg} color={t.text} minHeight="100vh" position="relative" overflow="hidden">
      <SEOHead pagePath="/" />
      <NavbarWithCallToAction />

      {/* Admin-only quick-jump grid to every module (opens each in a new tab). Self-gates on logged-in ADMIN. */}
      <AdminModuleLinks />

      <Box as="main">
        <Hero />
        <Problem />
        <Platform />
        <Modules />
        <Systems />
        <Chain />
        <StateOfPlay />
        <Social />
        <Pricing />
        <Program />
        <Token />
        <About />
        <Proof />
        <Contact />
      </Box>

      <Box id="footer-section">
        <FooterWithFourColumns />
      </Box>

      {/* Dev Section Badges - Purple badges for click-to-copy section IDs (dev only) */}
      <DevSectionBadges />
    </Box>
  );
};

export default Home;
